import { useEffect, useMemo, useRef, useState } from "react";
import { http, currency, formatApiError } from "@/lib/api";
import { X, Upload, CreditCard, Rocket, AlertCircle, CheckCircle2, ShieldCheck, LayoutGrid } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

// 4 preset Ad Size Packages. Each is a uniform NxN square expanding from the
// anchor cell the user clicked on the grid. `n` is the side in grid units;
// `blocks` is the total cell count (n²) which drives the Stripe price
// multiplier. Every claim deducts its full AREA (width × height) from the
// free-launch pool (backend `_promo_state()` sums this via aggregation).
const AD_SIZE_PACKAGES = [
  { blocks: 1,  n: 1, label: "1 Block",   sub: "10×10 pixels · Standard",       tag: "Starter",  price: 50 },
  { blocks: 4,  n: 2, label: "4 Blocks",  sub: "20×20 pixels · Medium Square",  tag: "Popular",  price: 200 },
  { blocks: 9,  n: 3, label: "9 Blocks",  sub: "30×30 pixels · Large Square",   tag: "Featured", price: 450 },
  { blocks: 16, n: 4, label: "16 Blocks", sub: "40×40 pixels · Mega Billboard", tag: "Pro",      price: 800 },
];

/**
 * Slide-out checkout panel. User clicks any empty cell on the grid → this
 * opens with that cell as the anchor. They pick an Ad Size Package (1 / 4 /
 * 9 / 16 blocks — uniform squares expanding from the anchor) and upload one
 * logo, which the frontend composites at hi-res and stretches across the
 * whole cluster with object-fit: cover.
 * - Free launch (first 50 claims): POSTs to /pixels/claim ($0).
 * - After promo: POSTs to /payments/checkout → Stripe (price scales with blocks).
 */
export default function ClaimPanel({ open, onClose, onReset, selection, promo, onClaimed }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [pkg, setPkg] = useState(AD_SIZE_PACKAGES[0]);
  const [check, setCheck] = useState(null);
  const [brand, setBrand] = useState("");
  const [url, setUrl] = useState("");
  const [slogan, setSlogan] = useState("");
  const [image, setImage] = useState(""); // data URL
  const [rawImage, setRawImage] = useState(""); // original data URL (pre-composite)
  const [bgColor, setBgColor] = useState("#ffffff");
  const [description, setDescription] = useState("");
  const [category, setCategory] = useState("");
  const [categories, setCategories] = useState([]);
  const [dragOver, setDragOver] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const fileRef = useRef(null);

  // Compute the effective selection. Two modes:
  // 1. Preset package (pkg.custom=false) — anchor cell expanded into NxN square,
  //    clamped to the 100×100 grid so a big package near the edge fits.
  // 2. Custom (pkg.custom=true) — use the raw drag selection dimensions as-is.
  const effectiveSelection = useMemo(() => {
    if (!selection) return null;
    if (pkg?.custom) {
      return { x: selection.x, y: selection.y, width: selection.width, height: selection.height };
    }
    const GRID = 100;
    const n = pkg.n;
    const x = Math.max(0, Math.min(selection.x, GRID - n));
    const y = Math.max(0, Math.min(selection.y, GRID - n));
    return { x, y, width: n, height: n };
  }, [selection, pkg]);

  // Auto-pick preset when the incoming selection matches one (drag→2×2, 3×3, 4×4)
  // or fall back to a synthetic "Custom" package that mirrors the drag dims.
  useEffect(() => {
    // Fetch category list once so the claim form can offer them as options.
    http.get("/config").then(({ data }) => setCategories(data.categories || [])).catch(() => {});
  }, []);

  useEffect(() => {
    if (!open || !selection) return;
    const w = selection.width, h = selection.height;
    const matched = AD_SIZE_PACKAGES.find((p) => p.n === w && p.n === h);
    if (matched) {
      setPkg(matched);
    } else if (w === 1 && h === 1) {
      setPkg(AD_SIZE_PACKAGES[0]);
    } else {
      setPkg({
        custom: true,
        n: null,
        blocks: w * h,
        label: `Custom ${w}×${h}`,
        sub: `${w * 10}×${h * 10} pixels · ${w * h} blocks`,
        tag: "Custom drag",
        price: w * h * 50,
      });
    }
  }, [open, selection?.x, selection?.y, selection?.width, selection?.height]);

  // Live availability + price whenever the selection or package changes.
  useEffect(() => {
    if (!open || !effectiveSelection) return;
    setError("");
    setCheck(null);
    http.get("/pixels/check", { params: effectiveSelection })
      .then(({ data }) => setCheck(data))
      .catch((e) => setError(formatApiError(e.response?.data?.detail)));
  }, [open, effectiveSelection]);

  const isFree = promo?.is_free_phase;
  const totalUnits = pkg.blocks;

  const validate = () => {
    if (!brand.trim()) return "Brand name is required";
    if (!url.trim()) return "Destination URL is required";
    try { new URL(url); } catch { return "Enter a valid URL (include https://)"; }
    if (slogan.length > 70) return "Slogan must be 70 characters or less";
    const words = description.trim().split(/\s+/).filter(Boolean);
    if (words.length > 50) return "Description must be 50 words or fewer";
    return "";
  };

  const submit = async () => {
    if (!user) {
      onClose?.();
      navigate("/login?next=/");
      return;
    }
    // Guard: during the free promo, a package larger than remaining pool is invalid.
    if (isFree && promo && pkg.blocks > promo.remaining) {
      const msg = "This size package exceeds the remaining free promotional blocks. Please select a smaller grid area.";
      toast.warning(msg, { duration: 4500 });
      setError(msg);
      return;
    }
    const v = validate();
    if (v) { setError(v); return; }
    setBusy(true); setError("");
    const payload = {
      ...effectiveSelection,
      brand_name: brand.trim(),
      destination_url: url.trim(),
      hover_slogan: slogan.trim(),
      description: description.trim(),
      category: category.trim(),
      image_url: image,
    };
    try {
      if (isFree) {
        const { data } = await http.post("/pixels/claim", payload);
        toast.success("🎉 Your block is LIVE — no payment needed!");
        onClaimed?.(data.block);
        onClose?.();
      } else {
        const { data } = await http.post("/payments/checkout", {
          ...payload, origin_url: window.location.origin,
        });
        window.location.href = data.checkout_url;
      }
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  };

  const readFile = (file) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) { setError("Only images allowed"); return; }
    if (file.size > 800 * 1024) { setError("Image must be ≤ 800 KB"); return; }
    const reader = new FileReader();
    reader.onload = () => {
      setRawImage(reader.result);
    };
    reader.readAsDataURL(file);
  };

  // High-resolution composite: "contain" fit + padded brand-color background.
  // Renders at up to 1200px on the longest side so logos stay crisp even when
  // users zoom into the grid on retina displays.
  useEffect(() => {
    if (!rawImage || !effectiveSelection) { return; }
    const img = new Image();
    img.onload = () => {
      const aspect = effectiveSelection.width / effectiveSelection.height;
      // Target the longer edge to ~2000 CSS px worth of resolution.
      const LONG = 2000;
      const targetW = aspect >= 1 ? LONG : Math.round(LONG * aspect);
      const targetH = aspect >= 1 ? Math.round(LONG / aspect) : LONG;
      const canvas = document.createElement("canvas");
      canvas.width = targetW;
      canvas.height = targetH;
      const ctx = canvas.getContext("2d");
      ctx.imageSmoothingEnabled = true;
      ctx.imageSmoothingQuality = "high";
      // Solid brand-color background (so transparent logos don't render on grid noise)
      ctx.fillStyle = bgColor;
      ctx.fillRect(0, 0, targetW, targetH);
      // "Contain" fit — never crops the logo, keeps aspect ratio, padded to target.
      const pad = 0.86; // 7% breathing room on all sides
      const scale = Math.min((targetW * pad) / img.width, (targetH * pad) / img.height);
      const w = img.width * scale, h = img.height * scale;
      const x = (targetW - w) / 2, y = (targetH - h) / 2;
      ctx.drawImage(img, x, y, w, h);
      setImage(canvas.toDataURL("image/jpeg", 0.94));
    };
    img.onerror = () => setError("Could not read image");
    img.src = rawImage;
  }, [rawImage, bgColor, effectiveSelection]);

  // Estimate logo pixel density → nudge users toward bigger blocks for clarity.
  const isSmallSelection = totalUnits < 4;

  if (!open || !selection || !effectiveSelection) return null;

  return (
    <>
      <div className={`slide-backdrop ${open ? "open" : ""}`} onClick={onClose} />
      <aside className={`slide-panel ${open ? "open" : ""}`} data-testid="claim-panel">
        <div className="p-6 border-b border-[var(--line-strong)]">
          <div className="flex items-start justify-between">
            <div>
              <div className="pill">{isFree ? "Launch promo · $0" : "Reserve block"}</div>
              <h2 className="mt-2 font-display text-3xl leading-none">
                Claim Your <span className="text-gradient">Internet Real Estate</span>
              </h2>
            </div>
            <button data-testid="close-claim-panel" onClick={onClose} className="p-2 rounded-full hover:bg-white/5">
              <X className="w-4 h-4" />
            </button>
          </div>
          <div className="mt-4 text-sm text-[var(--fg-muted)] font-mono">
            Anchor: ({effectiveSelection.x}, {effectiveSelection.y}) · {effectiveSelection.width}×{effectiveSelection.height} units
            · {effectiveSelection.width * 10}×{effectiveSelection.height * 10}px
          </div>
        </div>

        <div className="p-6 space-y-4">
          {/* Ad Size Package stepper — the core of the new numerical purchase
              flow. Picking a package expands the anchor cell into an N×N
              square. Total blocks drives the Stripe price multiplier. */}
          <div data-testid="package-picker">
            <div className="flex items-center gap-2">
              <LayoutGrid className="w-3.5 h-3.5 text-neon-violet" />
              <label className="text-xs uppercase tracking-widest text-[var(--fg-muted)]">Select Ad Size Package</label>
            </div>
            <div className="mt-2 grid grid-cols-2 gap-2">
              {AD_SIZE_PACKAGES.map((p) => {
                const active = pkg.blocks === p.blocks && !pkg.custom;
                const oversizedForFree = isFree && promo && p.blocks > (promo.remaining ?? 0);
                return (
                  <button
                    key={p.blocks}
                    type="button"
                    onClick={() => setPkg(p)}
                    disabled={oversizedForFree}
                    data-testid={`package-${p.blocks}`}
                    className={`text-left rounded-2xl border p-3 transition ${
                      active
                        ? "border-violet-400/70 bg-violet-500/10 shadow-[0_0_24px_rgba(139,92,246,0.25)]"
                        : "border-[var(--line-strong)] hover:border-white/20 hover:bg-white/[0.03]"
                    } ${oversizedForFree ? "opacity-40 cursor-not-allowed" : ""}`}
                  >
                    <div className="flex items-center justify-between">
                      <span className="font-display text-lg leading-none">{p.label}</span>
                      <span className={`text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full ${active ? "bg-violet-500/30 text-violet-100" : "bg-white/5 text-[var(--fg-muted)]"}`}>{p.tag}</span>
                    </div>
                    <div className="mt-1 text-xs text-[var(--fg-muted)]">{p.sub}</div>
                    <div className="mt-2 text-xs font-mono text-cyan-300">${p.price} · ×{p.blocks} block{p.blocks === 1 ? "" : "s"}</div>
                    {oversizedForFree && (
                      <div className="mt-1 text-[10px] uppercase tracking-widest text-amber-300">Exceeds free pool</div>
                    )}
                  </button>
                );
              })}
            </div>
            {pkg?.custom && (
              <div className="mt-2 rounded-2xl border border-cyan-400/50 bg-cyan-500/10 p-3" data-testid="custom-package-tile">
                <div className="flex items-center justify-between">
                  <span className="font-display text-lg leading-none">{pkg.label}</span>
                  <span className="text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full bg-cyan-500/30 text-cyan-100">Custom drag</span>
                </div>
                <div className="mt-1 text-xs text-[var(--fg-muted)]">{pkg.sub}</div>
                <div className="mt-2 text-xs font-mono text-cyan-300">${pkg.price} · ×{pkg.blocks} blocks</div>
                <div className="mt-1 text-[10px] uppercase tracking-widest text-cyan-300/70">Pick a preset above to swap · or keep this size</div>
              </div>
            )}
          </div>

          {/* Live preview + price */}
          {check && (
            <div className="rounded-2xl border border-[var(--line-strong)] p-4 bg-gradient-to-br from-violet-500/10 to-cyan-500/10">
              <div className="flex items-center justify-between">
                <span className="text-xs uppercase tracking-widest text-[var(--fg-muted)]">
                  {isFree ? "Your price" : `Total Pixels: ${check.pixels ?? totalUnits * 100} ($0.50 per pixel)`}
                </span>
                <span className="pill-cyan pill">{check.tier}</span>
              </div>
              <div className="mt-1 flex items-baseline gap-2">
                <span className="font-display text-4xl font-medium" data-testid="claim-price">
                  {isFree ? "$0" : currency(check.price)}
                </span>
                {!isFree && (
                  <span className="text-xs text-[var(--fg-muted)]">
                    · {totalUnits} block{totalUnits === 1 ? "" : "s"} × $50 flat
                  </span>
                )}
              </div>
              {isFree && (
                <div className="mt-2 text-xs text-cyan-300 flex items-center gap-1">
                  <Rocket className="w-3.5 h-3.5" /> FREE — no card, no Stripe. Auto-published.
                </div>
              )}
              <div className="mt-2 flex items-center gap-1.5 text-xs text-emerald-300 font-medium" data-testid="lifetime-badge">
                <ShieldCheck className="w-3.5 h-3.5" /> Owned for LIFETIME · One-time · No renewals ever
              </div>
              {!check.available && (
                <div className="mt-2 flex items-center gap-2 text-red-400 text-sm">
                  <AlertCircle className="w-4 h-4" /> Selection unavailable — try another area.
                </div>
              )}
            </div>
          )}

          <div>
            <label className="text-xs uppercase tracking-widest text-[var(--fg-muted)]">Company / Brand name</label>
            <input
              data-testid="input-brand"
              value={brand} onChange={(e) => setBrand(e.target.value)}
              className="input-lux mt-1" placeholder="Acme Robotics"
              maxLength={80}
            />
          </div>

          <div>
            <label className="text-xs uppercase tracking-widest text-[var(--fg-muted)]">Target destination URL</label>
            <input
              data-testid="input-url"
              value={url} onChange={(e) => setUrl(e.target.value)}
              className="input-lux mt-1" placeholder="https://your-site.com"
              type="url"
            />
          </div>

          <div>
            <div className="flex items-center justify-between">
              <label className="text-xs uppercase tracking-widest text-[var(--fg-muted)]">Hover slogan / pitch line</label>
              <span className="text-xs font-mono text-[var(--fg-muted)]">{slogan.length}/70</span>
            </div>
            <input
              data-testid="input-slogan"
              value={slogan} onChange={(e) => setSlogan(e.target.value.slice(0, 70))}
              className="input-lux mt-1" placeholder="We ship rockets, not features."
              maxLength={70}
            />
          </div>

          <div>
            <div className="flex items-center justify-between">
              <label className="text-xs uppercase tracking-widest text-[var(--fg-muted)]">
                Business description (SEO directory)
              </label>
              <span className="text-xs font-mono text-[var(--fg-muted)]" data-testid="desc-word-count">
                {description.trim() ? description.trim().split(/\s+/).filter(Boolean).length : 0}/50 words
              </span>
            </div>
            <textarea
              data-testid="input-description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className="input-lux mt-1 min-h-[92px] leading-relaxed"
              placeholder="One-paragraph description of what your business does. Shown on our public /directory page — helps search engines index you and drives real dofollow traffic. Max 50 words."
              rows={4}
            />
          </div>

          <div>
            <label className="text-xs uppercase tracking-widest text-[var(--fg-muted)]">
              Category
            </label>
            <select
              data-testid="input-category"
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              className="input-lux mt-1 w-full"
            >
              <option value="">Select a category…</option>
              {categories.map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </select>
            <p className="mt-1 text-xs text-[var(--fg-muted)]">
              Helps visitors filter the grid to find brands like yours.
            </p>
          </div>

          <div>
            <label className="text-xs uppercase tracking-widest text-[var(--fg-muted)]">Ad image</label>
            {isSmallSelection && (
              <div className="mt-1 flex items-start gap-2 rounded-lg border border-amber-400/30 bg-amber-400/10 px-3 py-2 text-xs text-amber-200" data-testid="small-selection-warning">
                <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                <span>Only 10×10px on a 1-block package. For a crisp logo pick the <b>4 Blocks</b> package or larger — wordmarks especially need width.</span>
              </div>
            )}
            <div
              data-testid="drop-zone"
              onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
              onDragLeave={() => setDragOver(false)}
              onDrop={(e) => { e.preventDefault(); setDragOver(false); readFile(e.dataTransfer.files?.[0]); }}
              onClick={() => fileRef.current?.click()}
              className={`drop-zone mt-1 cursor-pointer ${dragOver ? "drag-over" : ""}`}
            >
              {image ? (
                <div className="flex items-center gap-3">
                  <img src={image} alt="preview" className="w-16 h-16 object-contain rounded-lg border border-[var(--line-strong)]" style={{ background: bgColor }} />
                  <div className="text-sm">
                    <div className="font-semibold">Logo ready · 1200px source</div>
                    <div className="text-xs text-[var(--fg-muted)]">Auto-fit (no crop) · click to replace</div>
                  </div>
                </div>
              ) : (
                <>
                  <Upload className="w-6 h-6 mx-auto text-[var(--fg-muted)]" />
                  <div className="mt-2 text-sm">Drop image or click to upload</div>
                  <div className="text-xs text-[var(--fg-muted)]">PNG / JPG · ≤ 800 KB · fit without cropping</div>
                </>
              )}
              <input
                ref={fileRef} type="file" accept="image/*" hidden
                onChange={(e) => readFile(e.target.files?.[0])}
              />
            </div>
            {rawImage && (
              <div className="mt-2 flex items-center gap-3 text-xs text-[var(--fg-muted)]" data-testid="bg-color-row">
                <span className="uppercase tracking-widest">Background</span>
                <input
                  data-testid="bg-color-input"
                  type="color"
                  value={bgColor}
                  onChange={(e) => setBgColor(e.target.value)}
                  className="h-7 w-10 rounded border border-[var(--line-strong)] bg-transparent p-0 cursor-pointer"
                  aria-label="Logo background color"
                />
                <div className="flex gap-1">
                  {["#ffffff", "#000000", "#0a0a10", "#8b5cf6", "#06b6d4", "#f59e0b"].map((c) => (
                    <button
                      key={c}
                      type="button"
                      onClick={() => setBgColor(c)}
                      className="h-6 w-6 rounded-full border border-white/10 hover:scale-110 transition"
                      style={{ background: c }}
                      aria-label={`Set background ${c}`}
                      data-testid={`bg-swatch-${c.replace("#", "")}`}
                    />
                  ))}
                </div>
              </div>
            )}
          </div>

          {error && <div className="text-sm text-red-400" data-testid="claim-error">{error}</div>}

          <button
            data-testid="submit-claim"
            onClick={submit}
            disabled={busy || (check && !check.available)}
            className={`w-full ${isFree ? "btn-free" : "btn-primary"} disabled:opacity-50 disabled:cursor-not-allowed`}
          >
            {isFree ? <><Rocket className="w-4 h-4" /> Claim Free Launch Block ($0)</>
                    : <><CreditCard className="w-4 h-4" /> Pay {check ? currency(check.price) : ""} & Publish</>}
          </button>

          {/* Manual reset trap — closes the panel, wipes the selection state and
              clears the neon-purple highlight from the grid so the user can
              start a fresh drag without contamination from the previous one. */}
          <button
            type="button"
            data-testid="cancel-reset-selection"
            onClick={() => (onReset ? onReset() : onClose?.())}
            className="w-full text-center text-xs uppercase tracking-widest text-[var(--fg-muted)] hover:text-white transition py-2"
          >
            Cancel &amp; Reset Selection
          </button>

          {!user && (
            <p className="text-xs text-[var(--fg-muted)] flex items-start gap-2">
              <CheckCircle2 className="w-3.5 h-3.5 mt-0.5" />
                <span>You&apos;ll be asked to sign in before publishing.</span>
            </p>
          )}
        </div>
      </aside>
    </>
  );
}
